import { select, DisposalSet } from '@neptune/cx/utils';
import { registerFragment } from '@neptune/cx/registry';
import throttle from 'lodash/throttle';

import './styles.scss';
import './widgets/professional-profile-srt.scss';

import {
    reinitActiveFacets,
    removeFromMultiParamMap, replaceInMultiParamMap,
    toMultiParamMap,
    toURLSearchParams,
    updateResults
} from './facets';

const gc = new DisposalSet();

interface AySearchLayoutConfiguration {
    pageParameterName: string;
    queryParameterName: string;

    categoryParameterNames: string;
}


registerFragment<AySearchLayoutConfiguration>('ay-search-layout', (fragEl, config, meta) => {
    const searchLayoutEl = select<HTMLDivElement>(fragEl, '.ay-search-layout');
    const sortSelectEl = select<HTMLSelectElement>(searchLayoutEl, '.ay-search-layout__sort select');
    const facetsEl = select<HTMLDivElement>(searchLayoutEl, '.ay-search-layout__facets');
    const facetsToggleOpenEl = select<HTMLButtonElement>(searchLayoutEl, '.ay-search-layout__facets-toggle--open');
    const facetsToggleCloseEl = select<HTMLButtonElement>(searchLayoutEl, '.ay-search-layout__facets-toggle--close');

    const queryInputEl = select<HTMLInputElement>(searchLayoutEl, 'input.search-bar-keywords-input');
    const sortParamEl = (searchLayoutEl
         .querySelector('input.sort-parameter-name') as HTMLInputElement
     ) ?? null;

    const queryFormEl = queryInputEl?.closest('form') ?? null;

    const queryParamName = queryInputEl?.name || 'q';
    const sortParamName = sortParamEl?.value || 'sort';
    const pageParamName = config.pageParameterName || 'start';

    (gc.register(facetsToggleOpenEl)
        .addListener('click', () => {
            facetsEl.classList.add('ay-search-layout__facets--open');
        })
    );
    (gc.register(facetsToggleCloseEl)
        .addListener('click', () => {
            facetsEl.classList.remove('ay-search-layout__facets--open');
            searchLayoutEl.scrollIntoView({ behavior: 'instant', block: 'start' });
        })
    );


    if (sortSelectEl) {
        const sortHelperEl = document.createElement('span');
        sortHelperEl.classList.add('ay-search-layout__sort-helper');
        sortHelperEl.setAttribute('aria-hidden', 'true');
        sortSelectEl.parentElement?.appendChild(sortHelperEl);
        const resizeSort = () => {
            const selectedOptionEl = sortSelectEl.options[sortSelectEl.selectedIndex];
            sortHelperEl.innerHTML = selectedOptionEl.innerText;
            requestAnimationFrame(() => {
                sortSelectEl.style.setProperty(
                    "--ay-search-layout__sort--width",
                    `${sortHelperEl.offsetWidth}px`
                );
            })

        };
        resizeSort();
        setTimeout(resizeSort, 1000);
        setTimeout(resizeSort, 5000);

        (gc.register(sortSelectEl)
            .addListener('change', (event) => {
                resizeSort();
                const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
                const value = sortSelectEl.selectedOptions[0].value;
                if (!value || value.trim() === '') {
                    removeFromMultiParamMap(multiParamMap, sortParamName);
                } else {
                    replaceInMultiParamMap(multiParamMap, sortParamName, value);
                }
                removeFromMultiParamMap(multiParamMap, pageParamName, null);
                const params = toURLSearchParams(multiParamMap);
                const nextUrl = `${window.location.pathname}?${params}`;
                updateResults(searchLayoutEl, nextUrl, pageParamName);
            })
        );
    }


    if (queryInputEl && queryFormEl) {
        reinitActiveFacets(searchLayoutEl, queryInputEl, pageParamName);

        let lastQueryValue = queryInputEl.value;
        const onQueryUpdate = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (queryInputEl.value.trim() === lastQueryValue.trim()) {
                return;
            } // prevent unnecessary updates
            lastQueryValue = queryInputEl.value;
            const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
            const value = queryInputEl.value;
            if (!value || value.trim() === '') {
                removeFromMultiParamMap(multiParamMap, queryParamName);
            } else {
                replaceInMultiParamMap(multiParamMap, queryParamName, value);
            }
            removeFromMultiParamMap(multiParamMap, pageParamName, null);
            const params = toURLSearchParams(multiParamMap);
            const nextUrl = `${window.location.pathname}?${params}`;
            updateResults(searchLayoutEl, nextUrl, pageParamName);
        };
        const onQueryUpdateWrapped = throttle(onQueryUpdate, 500, { trailing: true, leading: true });

        (gc.register(queryInputEl)
            .addListener('keyup', onQueryUpdateWrapped)
            .addListener('blur', onQueryUpdateWrapped)
        );
        (gc.register(queryFormEl)
            .addListener('submit', e => {
                e.stopImmediatePropagation();
                onQueryUpdate(e);
            })
        );
    }

});

if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => gc.disposeAll());
}